/**
 * "Show to client" on safety register entries (MB's decision 1023, the Q895 follow-up B, 4 Nov 2026;
 * docs/specs/safety-register.md §10; Postgres `20261104230000_register_show_client.sql`). Every kind of record
 * (accidents, incidents, near misses, landholder complaints, and the two height breach kinds of decision 1021) can be
 * shown to the project's client, as the safety documents are (decision 926): tenant admins and managers choose; the
 * client reads them on the client portal's Safety page (Preview as client shows the same); nothing is shown until
 * someone chooses; "Hide from client" undoes it.
 *
 * The choice is Teslyn Cloud's (`safety_register_client`, keyed by the record's source table and id, so a height
 * breach is chosen against its breach report entry). The client reads only `my_safety_register`'s answer: the
 * columns below, never the rest (`CLIENT_REGISTER_NEVER_SHOWN`).
 */
import { heightBreachEntryLines, breachLineText, type HeightBreachFacts } from './height-breach.js';
import {
  REGISTER_KIND_WORDS,
  breachOccurredOn,
  safetyActionWords,
  type RegisterKind,
  type RegisterRecord,
} from './safety-register.js';

/** Who chooses which records the client sees (decision 1023: as the safety documents, tenant admins and managers). */
export const REGISTER_CLIENT_ROLES = ['tenant_admin', 'manager'] as const;

export function canShowRegisterToClient(roles: readonly string[] | null): boolean {
  return (roles ?? []).some((r) => (REGISTER_CLIENT_ROLES as readonly string[]).includes(r));
}

/** The table a register record comes from: the key of its choice with its id. */
export type RegisterRecordSource = 'safety_entry' | 'height_breach';

export function registerSourceOf(r: Pick<RegisterRecord, 'from_breach'>): RegisterRecordSource {
  return r.from_breach ? 'height_breach' : 'safety_entry';
}

/** A `safety_register_client` row as staff read it. */
export interface RegisterClientChoice {
  readonly id: string;
  readonly project_id: string;
  readonly source: RegisterRecordSource;
  readonly record_id: string;
  readonly shown: boolean;
  readonly shown_at: string | null;
  readonly shown_by_name: string;
  readonly hidden_at: string | null;
  readonly hidden_by_name: string;
}

/** What a client never reads of a register record (the database leaves it out of `my_safety_register`). */
export const CLIENT_REGISTER_NEVER_SHOWN = [
  'the follow-up and its due date',
  'the roles of the people involved and of who reported it',
  'the landholder’s name, details and phone',
  'the place on the map',
  'the linked photos',
  'who recorded or closed it',
] as const;

/** Whether a record can be shown now: live (not removed, not withdrawn from the breach report). */
export function registerShowable(r: Pick<RegisterRecord, 'deleted_at'>): boolean {
  return !r.deleted_at;
}

/** A record as `my_safety_register` answers it. */
export type ClientRegisterRow =
  | {
      readonly source: 'safety_entry';
      readonly id: string;
      readonly kind: RegisterKind;
      readonly occurred_on: string;
      readonly occurred_at: string;
      readonly topic: string;
      readonly reported_by_name: string;
      readonly people: readonly string[];
      readonly aircraft_registration: string;
      readonly location: string;
      readonly description: string;
      readonly actions: string;
      readonly corrective_action_taken: boolean;
      readonly closed_at: string | null;
      readonly shown_at: string;
    }
  | {
      readonly source: 'height_breach';
      readonly id: string;
      readonly kind: RegisterKind;
      readonly facts: HeightBreachFacts;
      readonly reason: string;
      readonly reported_by_name: string;
      readonly created_at: string;
      readonly shown_at: string;
    };

/** A record as the client portal shows it. */
export interface ClientRegisterRecord {
  readonly id: string;
  readonly source: RegisterRecordSource;
  readonly kind: RegisterKind;
  readonly kind_words: string;
  readonly occurred_on: string;
  readonly occurred_at: string;
  readonly topic: string;
  /** Label and value pairs, only those filled, in the order shown. */
  readonly lines: readonly (readonly [string, string])[];
  readonly closed_at: string | null;
  readonly shown_at: string;
}

const pad3 = (n: number): string => String(n).padStart(3, '0');

/** The client's records, newest first, from `my_safety_register`'s answer (anything else in a row is not shown). */
export function clientRegisterRecords(rows: readonly ClientRegisterRow[]): ClientRegisterRecord[] {
  const out = rows.map((r): ClientRegisterRecord => {
    if (r.source === 'height_breach') {
      const f = r.facts;
      const position =
        f.east !== null && f.north !== null ? `East ${f.east.toFixed(2)} North ${f.north.toFixed(2)}` : '';
      return {
        id: r.id,
        source: r.source,
        kind: r.kind,
        kind_words: REGISTER_KIND_WORDS[r.kind] ?? r.kind,
        occurred_on: breachOccurredOn(f.jday, r.created_at),
        occurred_at: f.local_time ?? '',
        topic: `Day ${f.jday} Flt${pad3(f.flight_number)}, Line ${breachLineText(f.line_name)}`,
        lines: (
          [
            ['Reported by', r.reported_by_name],
            ['Location', position],
            ['What happened', heightBreachEntryLines(f).join('\n')],
            [safetyActionWords(r.kind), r.reason.trim()],
          ] as [string, string][]
        ).filter(([, v]) => v.trim() !== ''),
        closed_at: r.created_at,
        shown_at: r.shown_at,
      };
    }
    return {
      id: r.id,
      source: r.source,
      kind: r.kind,
      kind_words: REGISTER_KIND_WORDS[r.kind] ?? r.kind,
      occurred_on: String(r.occurred_on).slice(0, 10),
      occurred_at: r.occurred_at,
      topic: r.topic,
      lines: (
        [
          ['Reported by', r.reported_by_name],
          ['People involved', (r.people ?? []).join(', ')],
          ['Aircraft', r.aircraft_registration],
          ['Location', r.location],
          ['What happened', r.description],
          [safetyActionWords(r.kind), r.actions],
          ['Corrective action has been taken', r.corrective_action_taken ? 'Yes' : 'No'],
        ] as [string, string][]
      ).filter(([, v]) => (v ?? '').trim() !== ''),
      closed_at: r.closed_at,
      shown_at: r.shown_at,
    };
  });
  return out.sort(
    (a, b) =>
      b.occurred_on.localeCompare(a.occurred_on) ||
      b.occurred_at.localeCompare(a.occurred_at) ||
      b.shown_at.localeCompare(a.shown_at),
  );
}
